import type { Location } from '#/graphql/types';
import type { ColumnProps } from '#/common/components/DataGrid';
import TMMButton from '#/common/components/TMMButton';
import { levelsList, Level } from '../Location.Mappings';
import { inUseStatuses } from '../../utils/constants';
import { matchStatuses } from '../../../utils/constants';
import { gql } from '@apollo/client';
import { getTenantList } from '#/graphql/queries';
import {
  SelectLabel,
  MatchingItemLabel,
  MatchingItemCell,
} from '../../../utils/MatchingColumnsStyles';
import { dataSourceList, dataSources } from '#/modules/admin/tenants/utils/mappings';

export interface LocationDataGridColumnsOptions {
  onEditMatch?: (location: Location) => void;
  isTechnician?: boolean;
}

export const createLocationColumns = (
  options: LocationDataGridColumnsOptions = {},
): ColumnProps<Location>[] => [
  { dataField: 'id', caption: 'ID', visible: false, showInColumnChooser: false },
  {
    dataField: 'name',
    caption: 'Name',
    dataType: 'string',
    visible: true,
    fixed: true,
    openOnClick: true,
  },
  {
    dataField: 'level',
    caption: 'Type',
    dataType: 'string',
    visible: true,
    lookup: {
      dataSource: levelsList,
      valueExpr: 'value',
      displayExpr: 'label',
    },
  },
  {
    dataField: 'parent.name',
    caption: 'Parent',
    dataType: 'string',
    visible: true,
  },
  {
    dataField: 'inUseStatus',
    caption: 'Status',
    dataType: 'string',
    visible: false,
    lookup: {
      nullable: true,
      dataSource: Object.values(inUseStatuses),
      displayExpr: 'label',
      valueExpr: 'key',
    },
  },
  {
    dataField: 'matchedLocation',
    caption: 'Matching Location',
    dataType: 'string',
    allowFiltering: false,
    allowSearch: false,
    allowSorting: false,
    visible: true,
    width: 100,
    cellRender: (e) => {
      const item = e.data;
      const matchedLocation = item?.matchedLocation;
      const isFsmDataSource =
        item?.tenant?.dataSource === matchStatuses.fsm.key && !!item?.tenant?.dataSourceId;

      if (
        (item.matchStatus === matchStatuses.fsm.key ||
          item.matchStatus === matchStatuses.matched.key) &&
        isFsmDataSource &&
        item.level === Level.Building
      ) {
        const label = matchedLocation ?? <SelectLabel>Select</SelectLabel>;

        if (!options.onEditMatch || options.isTechnician) {
          return <MatchingItemLabel>{label}</MatchingItemLabel>;
        }

        return (
          <MatchingItemCell>
            <MatchingItemLabel>{label}</MatchingItemLabel>
            <TMMButton icon="pen" type="secondary" onClick={() => options.onEditMatch?.(item)} />
          </MatchingItemCell>
        );
      }
    },
  },
  {
    dataField: 'tenant.name',
    caption: 'Organization',
    visible: true,
    dataType: 'string',
    headerFilter: {
      getItemList: gql(getTenantList),
      displayExpr: 'name',
    },
  },
  {
    dataField: 'matchStatus',
    caption: 'Source',
    dataType: 'string',
    visible: false,
    allowFiltering: false,
    lookup: {
      nullable: true,
      dataSource: dataSourceList,
      displayExpr: 'label',
      valueExpr: 'key',
    },
    calcFilterExpression: (value, operation) => {
      // operation is always equals as we support only lookup filter
      if (value === dataSources.fsm.key) {
        // both matched and FSM
        return ['thirdPartyId2', '<>', null];
      } else if (value === dataSources.tis.key) {
        // only TIS
        return [['thirdPartyId', '<>', null], 'and', ['thirdPartyId2', '=', null]];
      }
      // no TIS, no FSM
      return [['thirdPartyId', '=', null], 'and', ['thirdPartyId2', '=', null]];
    },
    cellRender: (e) => {
      const item = e.data;
      const matchStatus = item?.matchStatus;
      if (matchStatus === matchStatuses.fsm.key || matchStatus === matchStatuses.matched.key) {
        return 'FSM';
      } else if (!matchStatus) {
        return '-';
      } else {
        return 'TC';
      }
    },
  },
];

const columns = createLocationColumns();

export default columns;
